import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, XCircle, Gift } from "lucide-react";

const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const bookingIdParam = params.get("booking_id");
  const [state, setState] = useState({ status: "polling", message: "Vérification du paiement…" });
  const [amount, setAmount] = useState(null);
  const [bookingId, setBookingId] = useState(bookingIdParam || null);
  const [isGift, setIsGift] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Session de paiement invalide." });
      return;
    }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setAmount(data.amount_total / 100);
          setBookingId(data.booking_id || null);
          setIsGift(!!data.is_gift);
          setState({ status: "paid", message: "Paiement réussi ! Votre réservation est confirmée." });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", message: "Session de paiement expirée." });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", message: "Le traitement prend plus longtemps que prévu. Votre confirmation arrivera par email." });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        // Transient errors (Cloudflare 5xx, network blips) — keep polling silently
        const status = err?.response?.status;
        const isClientError = status >= 400 && status < 500;
        if (isClientError || attempts >= MAX_ATTEMPTS) {
          setState({
            status: attempts >= MAX_ATTEMPTS ? "timeout" : "error",
            message: attempts >= MAX_ATTEMPTS
              ? "Le traitement prend plus longtemps que prévu. Votre confirmation arrivera par email."
              : (formatApiError(err.response?.data?.detail) || "Erreur lors de la vérification."),
          });
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {state.status === "polling" && (
        <>
          <Loader2 className="w-12 h-12 text-primary mx-auto mb-6 animate-spin" />
          <h1 className="font-display text-4xl mb-4">Paiement en cours…</h1>
          <p className="text-muted-foreground">{state.message}</p>
        </>
      )}

      {state.status === "paid" && (
        <div data-testid="payment-success-confirmed">
          <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-6" />
          <h1 className="font-display text-5xl mb-4">Merci !</h1>
          <p className="text-muted-foreground text-lg mb-2">Votre réservation est confirmée.</p>
          {amount && <p className="text-2xl gold-text font-display mb-8">{amount.toFixed(2)} €</p>}
          {isGift && bookingId && (
            <div className="mx-auto max-w-md mb-8 rounded-xl border border-primary/30 bg-primary/5 p-6 text-left" data-testid="egift-pdf-block">
              <div className="flex items-center gap-3 mb-2">
                <Gift className="w-5 h-5 text-primary" />
                <span className="text-xs uppercase tracking-[0.2em] text-primary">E-cadeau prêt</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Téléchargez l&apos;e-cadeau personnalisé pour l&apos;offrir à votre proche
                (à imprimer ou à partager par email / WhatsApp).
              </p>
              <Button
                asChild
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11"
                data-testid="download-egift-btn"
              >
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/event-bookings/${bookingId}/egift.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🎁 Télécharger l&apos;e-cadeau (PDF)
                </a>
              </Button>
            </div>
          )}
          <div className="flex gap-3 justify-center flex-wrap">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12 px-8" data-testid="success-see-bookings-btn">
              <Link to="/compte">Voir mes réservations</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full h-12 px-8">
              <Link to="/evenements">Autres événements</Link>
            </Button>
          </div>
        </div>
      )}

      {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
        <div data-testid="payment-success-error">
          <XCircle className="w-16 h-16 text-destructive mx-auto mb-6" />
          <h1 className="font-display text-4xl mb-4">
            {state.status === "timeout" ? "Traitement en cours" : "Problème de paiement"}
          </h1>
          <p className="text-muted-foreground mb-8">{state.message}</p>
          <Button asChild variant="outline" className="rounded-full h-12 px-8">
            <Link to="/compte">Voir mes réservations</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
